import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  Switch,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Box,
  HStack,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useState } from 'react';
import { UseFormRegister, FieldError, RegisterOptions } from 'react-hook-form';

interface FormFieldProps {
  label: string;
  name: string;
  register: UseFormRegister<any>;
  error?: FieldError;
  type?: 'text' | 'email' | 'password' | 'select' | 'textarea' | 'switch' | 'tel' | 'date' | 'number';
  options?: { value: string; label: string, selected?: boolean }[];
  placeholder?: string;
  required?: boolean;
  description?: string;
  maxLength?: number;
  min?: number;
  max?: number | string;
  step?: number;
  value?: string | number;
  onChange?: (value: string | number) => void;
  testId?: string;
}

export const FormField = ({
  label,
  name,
  register,
  error,
  type = 'text',
  options = [],
  placeholder,
  required = false,
  description,
  maxLength,
  min,
  max,
  step,
  value,
  onChange,
  testId,
}: FormFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [charCount, setCharCount] = useState(value?.toString().length || 0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) {
      e.target.value = newValue.slice(0, maxLength);
      return;
    }
    setCharCount(e.target.value.length);
    onChange?.(type === 'number' ? parseFloat(e.target.value) : e.target.value);
  };

  const registerOptions: RegisterOptions = {
    onChange: handleChange,
    maxLength: maxLength,
    ...(type === 'number' && {
      valueAsNumber: true,
      min: {
        value: min || -Infinity,
        message: `Value must be greater than ${min}`,
      },
      max: {
        value: max || Infinity,
        message: `Value must be less than ${max}`,
      },
    }),
  };

  const registerProps = register(name, registerOptions);

  const renderField = () => {
    switch (type) {
      case 'password':
        return (
          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              {...registerProps}
              data-testid={testId}
              placeholder={placeholder}
              bg="bg-surface"
              borderColor="border-default"
              _hover={{ borderColor: "border-hover" }}
              _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
            />
            <InputRightElement>
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                icon={showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
                onClick={() => setShowPassword(!showPassword)}
                variant="ghost"
                size="sm"
              />
            </InputRightElement>
          </InputGroup>
        );
      case 'select':
        return (
          <Select
            {...registerProps}
            data-testid={testId}
            placeholder={placeholder}
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: "border-hover" }}
            _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value} {...(option?.selected ? { selected: true } : {})}>
                {option.label}
              </option>
            ))}
          </Select>
        );
      case 'textarea':
        return (
          <Box>
            <Textarea
              {...registerProps}
              data-testid={testId}
              placeholder={placeholder}
              bg="bg-surface"
              borderColor="border-default"
              _hover={{ borderColor: "border-hover" }}
              _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
              rows={4}
            />
            {maxLength && (
              <Text fontSize="sm" color="text-muted" textAlign="right" mt={1}>
                {charCount}/{maxLength}
              </Text>
            )}
          </Box>
        );
      case 'switch':
        return (
          <Switch
            {...registerProps}
            data-testid={testId}
            size="lg"
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            {...registerProps}
            data-testid={testId}
            placeholder={placeholder}
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: "border-hover" }}
            _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
            min={min}
            max={max}
            step={step}
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            {...registerProps}
            data-testid={testId}
            placeholder={placeholder}
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: "border-hover" }}
            _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
            max={max}
          />
        );
      default:
        return (
          <Box>
            <Input
              type={type}
              {...registerProps}
              placeholder={placeholder}
              bg="bg-surface"
              borderColor="border-default"
              _hover={{ borderColor: "border-hover" }}
              _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
              data-testid={testId}
            />
            {maxLength && (
              <Text fontSize="sm" color="text-muted" textAlign="right" mt={1}>
                {charCount}/{maxLength}
              </Text>
            )}
          </Box>
        );
    }
  };

  return (
    <FormControl isInvalid={!!error} isRequired={required}>
      <HStack justify="space-between" mb={description ? 1 : 3}>
        <FormLabel color="text-primary" mb={0}>
          {label}
        </FormLabel>
      </HStack>
      {description && (
        <Text fontSize="sm" color="text-secondary" mb={3}>
          {description}
        </Text>
      )}
      {renderField()}
      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  );
};
